/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperSearch } from '@/scapi';
import type { BadgeDetail } from '@/types/config';

interface GetProductBadgesProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    badgeDetails: BadgeDetail[];
    maxBadges?: number;
}

const PRODUCT_FLAGS_PROPERTY = 'c_productFlags';
const PRODUCT_FLAGS_DISPLAY_PROPERTY = 'c_productFlagsDisplayValue';

// RGBARRY-CUSTOMIZATION: use Product Flags as a merchant-authored fallback only when no standard
// product badge applies; the response hook supplies the exact localized Business Manager label.
const productFlagFallbackLabels: Record<string, string> = {
    sellingfast: 'Selling Fast',
};

const nonEmptyString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

const findCustomPropertyValue = (product: ShopperSearch.schemas['ProductSearchHit'], propertyName: string): unknown => {
    const customProperties = product.customProperties as Array<{ id?: string; value?: unknown }> | undefined;
    if (!Array.isArray(customProperties)) return undefined;
    return customProperties.find((property) => property.id?.toLowerCase() === propertyName.toLowerCase())?.value;
};

const humanizeProductFlag = (value: string): string =>
    productFlagFallbackLabels[value.toLowerCase()] ||
    value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());

const getProductFlagBadge = (
    product: ShopperSearch.schemas['ProductSearchHit'] | ({ [key: string]: unknown } & { representedProduct?: unknown })
): BadgeDetail | undefined => {
    const record = product as Record<string, unknown>;
    const representedProduct = (record.representedProduct as Record<string, unknown> | null | undefined) ?? undefined;
    const searchHit = product as ShopperSearch.schemas['ProductSearchHit'];
    const displayValue =
        nonEmptyString(record[PRODUCT_FLAGS_DISPLAY_PROPERTY]) ||
        nonEmptyString(representedProduct?.[PRODUCT_FLAGS_DISPLAY_PROPERTY]) ||
        nonEmptyString(findCustomPropertyValue(searchHit, PRODUCT_FLAGS_DISPLAY_PROPERTY));
    const storedValue =
        nonEmptyString(record[PRODUCT_FLAGS_PROPERTY]) ||
        nonEmptyString(representedProduct?.[PRODUCT_FLAGS_PROPERTY]) ||
        nonEmptyString(findCustomPropertyValue(searchHit, PRODUCT_FLAGS_PROPERTY));
    const label = displayValue || (storedValue && humanizeProductFlag(storedValue));

    return label
        ? {
              propertyName: PRODUCT_FLAGS_PROPERTY,
              label,
              color: 'orange',
              priority: 0,
          }
        : undefined;
};
// END RGBARRY-CUSTOMIZATION

/**
 * Static utility function to determine which product badges should be displayed.
 *
 * NOTE: badgeDetails must be provided from runtime config (use useConfig() in components)
 *
 * @param props - Configuration for badge determination
 * @returns Object containing badges array and hasBadges boolean
 */
export const getProductBadges = ({ product, badgeDetails, maxBadges = 3 }: GetProductBadgesProps) => {
    if (!product) return { badges: [], hasBadges: false };

    const activeBadges: BadgeDetail[] = [];

    // Check representedProduct properties (where c_isSale is located)
    if (product.representedProduct) {
        badgeDetails.forEach((badge) => {
            const propertyValue = (product.representedProduct as Record<string, unknown>)[badge.propertyName];
            if (shouldShowBadge(propertyValue)) {
                activeBadges.push(badge);
            }
        });
    }

    // Check custom properties as fallback
    const customProperties = product.customProperties as Array<{ id?: string; value?: unknown }> | undefined;
    if (customProperties && Array.isArray(customProperties)) {
        badgeDetails.forEach((badge) => {
            // Skip if badge already found in representedProduct
            if (activeBadges.find((b) => b.propertyName === badge.propertyName)) {
                return;
            }

            const customProp = customProperties.find(
                (prop) => prop.id === badge.propertyName || prop.id?.toLowerCase() === badge.propertyName.toLowerCase()
            );

            if (customProp && shouldShowBadge(customProp.value)) {
                activeBadges.push(badge);
            }
        });
    }

    // Check promotions for sale badge (if not already found)
    const promotions = product.promotions as Array<unknown> | undefined;
    if (promotions && promotions.length > 0) {
        const saleBadge = badgeDetails.find((badge) => badge.propertyName === 'c_isSale');
        if (saleBadge && !activeBadges.find((badge) => badge.propertyName === 'c_isSale')) {
            activeBadges.push(saleBadge);
        }
    }

    // Check if product is out of stock
    if (product.inStock === false) {
        const outOfStockBadge = badgeDetails.find((badge) => badge.propertyName === 'c_isOutOfStock');
        if (outOfStockBadge && !activeBadges.find((badge) => badge.propertyName === 'c_isOutOfStock')) {
            activeBadges.push(outOfStockBadge);
        }
    }

    // RGBARRY-CUSTOMIZATION: Product Flags never compete with configured, promotional, or stock badges.
    if (activeBadges.length === 0) {
        const productFlagBadge = getProductFlagBadge(product);
        if (productFlagBadge) activeBadges.push(productFlagBadge);
    }
    // END RGBARRY-CUSTOMIZATION

    // Sort by priority (higher priority first)
    const badges = activeBadges.sort((a, b) => (b.priority || 0) - (a.priority || 0)).slice(0, maxBadges);

    return {
        badges,
        hasBadges: badges.length > 0,
    };
};

// Helper function to check if a property should show a badge
const shouldShowBadge = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        return value === 'true' || value === '1' || value.toLowerCase() === 'yes';
    }
    if (typeof value === 'number') return value === 1;
    return false;
};

/**
 * PDP variant: reads badge-driving custom attributes off a full `Product`
 * (top-level `c_*` fields), instead of the search-hit nested shape.
 */
export const getPdpProductBadges = ({
    product,
    badgeDetails,
    maxBadges = 3,
}: {
    product: { [key: string]: unknown; promotions?: unknown[]; inStock?: boolean } | null | undefined;
    badgeDetails: BadgeDetail[];
    maxBadges?: number;
}) => {
    if (!product) return { badges: [], hasBadges: false };
    const active: BadgeDetail[] = [];
    badgeDetails.forEach((badge) => {
        if (shouldShowBadge(product[badge.propertyName])) {
            active.push(badge);
        }
    });
    if (Array.isArray(product.promotions) && product.promotions.length > 0) {
        const saleBadge = badgeDetails.find((b) => b.propertyName === 'c_isSale');
        if (saleBadge && !active.find((b) => b.propertyName === 'c_isSale')) {
            active.push(saleBadge);
        }
    }
    if (product.inStock === false) {
        const oos = badgeDetails.find((b) => b.propertyName === 'c_isOutOfStock');
        if (oos && !active.find((b) => b.propertyName === 'c_isOutOfStock')) {
            active.push(oos);
        }
    }
    // RGBARRY-CUSTOMIZATION: mirror the PLP fallback on PDP image badges without changing normal precedence.
    if (active.length === 0) {
        const productFlagBadge = getProductFlagBadge(product);
        if (productFlagBadge) active.push(productFlagBadge);
    }
    // END RGBARRY-CUSTOMIZATION
    const badges = active.sort((a, b) => (b.priority || 0) - (a.priority || 0)).slice(0, maxBadges);
    return { badges, hasBadges: badges.length > 0 };
};
